<template>
  <div class="group-order-recipient-info">
    <div v-if="!loadStatus">
      <Skeleton width="100%" height="3.5rem" v-for="i in 15" :key="i" />
    </div>
    <template>
      <RemoveRecipientsModal
        :showFooter="true"
        :modalName="removeRecipientsModalName"
        :buttonLabel="'Remove'"
        v-show="isModalActive"
        @confirmAction="confirmRemoveRecipients($event)"
        colorVariation="secondary"
      >
        <template slot="header">
          Remove Recipient(s)
        </template>
        <template slot="body">
          <p class="modal-description">
            Are you sure you want to remove the selected recipient(s)?
          </p>
        </template>
      </RemoveRecipientsModal>

      <div class="order-list">
        <!-- Filter Region Here -->
        <div class="filters">
          <div class="search-region">
            <SearchAndFilter :placeholder="'Search by email or name'" />
          </div>

          <v-select
            class="light"
            :options="filterOptions"
            :clearable="false"
            :searchable="false"
            v-model="currentFilter"
            @input="setSelected"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"
                ><svg
                  width="9"
                  height="5"
                  viewBox="0 0 9 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.5 5C4.38766 5 4.27531 4.95646 4.18974 4.86983L0.128691 0.758491C-0.0428968 0.584779 -0.0428968 0.303996 0.128691 0.130284C0.300278 -0.043428 0.577626 -0.043428 0.749214 0.130284L4.5 3.92752L8.25079 0.130284C8.42237 -0.043428 8.69972 -0.043428 8.87131 0.130284C9.0429 0.303996 9.0429 0.584779 8.87131 0.758491L4.81026 4.86983C4.72469 4.95646 4.61234 5 4.5 5Z"
                    fill="#25282A"
                  />
                </svg>
              </span>
            </template>
          </v-select>
        </div>

        <!-- Action Button Region here-->
        <div class="recipients-tab-menu" v-if="loadStatus">
          <ul>
            <li
              v-if="groupOrderDetails.state !== 'finalized'"
              class="icon-action-btn"
            >
              <img
                v-tooltip.bottom="{
                  content: 'Skip',
                  theme: 'action-btn'
                }"
                alt="skip"
                @click="skipRecipientsFromGroupOrder"
                :class="{
                  inactive:
                    !groupOrderRecipientsList.length || !enabledActionButton
                }"
                :src="require('../../assets/skip-enabled.svg')"
              />
            </li>
            <li
              v-if="
                groupOrderDetails.state !== 'finalized' || !isOrderFinalized
              "
              class="icon-action-btn"
            >
              <img
                v-tooltip.bottom="{
                  content: 'Remove'
                }"
                @click="
                  {
                    !isOrderFinalized
                      ? removeRecipientsFromGroupOrder(
                          removeRecipientsModalName
                        )
                      : ''
                  }
                "
                :class="{
                  inactive:
                    !groupOrderRecipientsList.length ||
                    !enabledActionButton ||
                    isOrderFinalized
                }"
                :src="require('../../assets/delete-enabled.svg')"
              />
            </li>
            <li>
              <ExportToCSVButton
                :buttonLabel="'Export'"
                buttonType="tiny"
                :isPrimaryButton="false"
                :fileName="
                  `${groupOrderDetails.id}-${groupOrderDetails.name}-recipients.csv`
                "
                :data="exportData"
                customStyles="font-size: 0.75rem"
                :isGray="true"
              />
            </li>
          </ul>
        </div>

        <!-- Table Here -->
        <vue-good-table
          :columns="recipientColumns"
          :rows="groupOrderRecipientsList"
          styleClass="vgt-table striped"
          :search-options="{
            enabled: false
          }"
          @on-cell-click="onCellClick"
        >
          <div slot="emptystate" class="empty-state">
            There are no items to display.
          </div>
          <template slot="table-row" slot-scope="groupOrderRecipientsList">
            <div v-if="groupOrderRecipientsList.column.field === 'action'">
              <label
                class="checkbox-container"
                v-if="
                  isIncludedInLitsToRemove(
                    parseInt(groupOrderRecipientsList.row.id)
                  )
                "
              >
                <input type="checkbox" disabled checked />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container" v-else>
                <input type="checkbox" disabled />
                <span class="checkmark"></span>
              </label>
            </div>

            <span
              v-else-if="groupOrderRecipientsList.column.field === 'address'"
            >
              <span class="enter-line">{{
                getFullAddress(groupOrderRecipientsList.row)
              }}</span>
            </span>
            <span v-else-if="groupOrderRecipientsList.column.field === 'email'">
              <span class="email">{{
                groupOrderRecipientsList &&
                groupOrderRecipientsList.row &&
                groupOrderRecipientsList.row
                  ? groupOrderRecipientsList.row.email
                  : ''
              }}</span>
            </span>
            <span v-else-if="groupOrderRecipientsList.column.field === 'items'">
              <span
                class="enter-line"
                v-for="{ productName, id } in groupOrderRecipientsList.row
                  .groupOrderTierItems"
                :key="id"
              >
                {{ `• ${productName}` }}
              </span>
            </span>
          </template>
        </vue-good-table>
      </div>
    </template>
    <Pagination
      :total-items="totalRecords"
      :page="serverParams.page"
      :items-per-page="serverParams.perPage"
      @page-change="pageChange"
      @perPageChange="handlePerPageChange"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { progressStatus, recipientListFilterTypes } from '../../helpers'
import { VueGoodTable } from 'vue-good-table'
import DateFns from '@/mixins/DateFns'
import ExportToCSVButton from '../Universal/ExportToCSVButton'
import SearchAndFilter from '@/components/Universal/SearchAndFilter'

export default {
  name: 'GroupOrderRecipientListTab',
  props: {
    groupOrderId: {
      type: Number,
      required: true
    },
    groupOrderDetails: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  mixins: [DateFns],
  components: {
    VueGoodTable,
    ExportToCSVButton,
    SearchAndFilter,
    RemoveRecipientsModal: () => import('@/components/Universal/RemoveModal'),
    Skeleton: () => import('@/components/Universal/Skeleton'),
    Pagination: () => import('@/components/Universal/Pagination')
  },

  data() {
    return {
      check: false,
      removeRecipientsModalName: 'removeRecipientsModal',
      filterOptions: ['Submitted', 'Pending', 'Finalized', 'Skipped', 'All'],
      currentFilter: 'All',
      recipientColumns: [
        {
          label: '',
          field: 'action',
          sortable: false,
          width: '5%'
        },
        {
          label: 'Email',
          field: 'email',
          width: '10%'
        },
        {
          label: 'Name',
          field: 'name',
          width: '20%'
        },
        {
          label: 'Address',
          field: 'address',
          width: '20%'
        },
        {
          label: 'Selected Items',
          field: 'items',
          width: '40%'
        },
        {
          label: 'Status',
          field: 'recipientState',
          width: '5%'
        }
      ],
      cellIndexClicked: null,
      attributes: {
        ref: 'openIndicator',
        role: 'presentation',
        class: 'light'
      },
      serverParams: {
        page: 0,
        perPage: 50
      }
    }
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadGroupOrderRecipientsList)
    },
    isOrderFinalized() {
      const orders = this.groupOrderRecipientsList.filter(recipient =>
        this.recipientsToSkipOrRemove.some(
          skip => skip.id === parseInt(recipient.id)
        )
      )
      return orders.some(o => o.recipientState === 'Finalised')
    },
    ...mapGetters({
      activeModals: 'modal/activeModals'
    }),
    ...mapState({
      loadGroupOrderRecipientsList: state =>
        state.groupOrder.loadGroupOrderRecipientsList,
      groupOrderRecipientsList: state =>
        state.groupOrder.groupOrderRecipientsList,
      recipientsGroupOrderSelectedItems: state =>
        state.groupOrder.recipientsGroupOrderSelectedItems,
      recipientsToSkipOrRemove: state =>
        state.groupOrder.recipientsToSkipOrRemove,
      searchTerm: state => state.globalTracking.searchTerm,
      totalRecords: state => state.groupOrder.recipientTotal
    }),
    enabledActionButton() {
      return !!this.recipientsToSkipOrRemove.length
    },
    isModalActive() {
      return this.activeModals(this.removeRecipientsModalName)
    },
    exportData() {
      let data = []
      data = this.groupOrderRecipientsList.map(recipient => {
        return {
          ['Email']: recipient.email,
          ['Name']: recipient.name,
          ['Address_1']: recipient.addressOne ? recipient.addressOne : '',
          ['Address_2']: recipient.addressTwo ? recipient.addressTwo : '',
          ['City']: recipient.city ? recipient.city : '',
          ['State']: recipient.state ? recipient.state : '',
          ['Zipcode']: recipient.zipcode ? recipient.zipcode : '',
          ['Submitted']: recipient['groupOrderConfirmed?'] ? 'Yes' : 'No',
          ['Items']: this.getRecipientItems(recipient.groupOrderTierItems),
          ['Skipped']: recipient.recipientState ? 'No' : 'Yes',
          customFields: recipient.customFields
        }
      })

      let jsonData = []
      for (let index = 0; index < data.length; index++) {
        let recipient = data[index]
        let fields = {}

        for (let rIndex = 0; rIndex < recipient.customFields.length; rIndex++) {
          let { name, value } = recipient.customFields[rIndex]
          fields = { ...fields, [name]: value }
        }

        recipient = Object.assign(recipient, fields)
        delete recipient.customFields
        jsonData.push(recipient)
      }

      return jsonData
    }
  },

  created() {
    this.getGroupOrdersRecipientsList({
      groupOrderId: this.groupOrderId,
      filterBy: this.currentFilter,
      page: 1,
      perPage: this.serverParams.perPage
    })
  },
  methods: {
    ...mapActions('groupOrder', ['getGroupOrdersRecipientsList']),
    pageChange(page) {
      this.updateParams({ page: page })
      this.getNextChuckOfRecipientList()
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps)
    },
    handlePerPageChange(e) {
      this.serverParams.perPage = e
      this.getNextChuckOfRecipientList()
    },
    getNextChuckOfRecipientList() {
      this.getGroupOrdersRecipientsList({
        groupOrderId: this.groupOrderId,
        filterBy: this.currentFilter,
        page: this.serverParams.page + 1,
        perPage: this.serverParams.perPage
      })
    },
    getFullAddress(row) {
      if (row.addressTwo) {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.addressTwo ? row.addressTwo : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      } else {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      }
    },
    getRecipientItems(data) {
      if (data.length) {
        return data.map(variant => `• ${variant.productName}`).join('\n')
      }
    },
    onCellClick({ column, row }) {
      // let index
      // params.row - row object
      // params.column - column object
      // params.rowIndex - index of this row on the current page.
      // params.event - click event

      switch (column.field) {
        case 'action':
          // if (row.recipientState === 'finalized' || row['hasOrder?']) {
          if (
            row.recipientState === 'finalized' ||
            row.recipientState === 'Finalized'
          ) {
            this.$store.dispatch(
              'notification/pushNotification',
              {
                type: 'error',
                messages: ['Finalized recipients cannot be removed or skipped.']
              },
              { root: true }
            )
            return
          } else {
            this.$store.dispatch(
              'groupOrder/addRecipientToBeSkippedOrRemovedFromGO',
              parseInt(row.id)
            )
          }

          break
      }
    },
    isIncludedInLitsToRemove(id) {
      let recipient = this.recipientsToSkipOrRemove.find(user => user.id === id)

      if (recipient) {
        return true
      }

      return false
    },
    removeRecipientsFromGroupOrder(modalName) {
      if (this.recipientsToSkipOrRemove.length) {
        this.$store.dispatch('modal/showRemoveModal', modalName)
      }
    },
    confirmRemoveRecipients() {
      this.$store.dispatch(
        'groupOrder/removeRecipientsFromGroupOrder',
        this.groupOrderId
      )
    },
    async skipRecipientsFromGroupOrder() {
      if (this.recipientsToSkipOrRemove.length) {
        await this.$store.dispatch(
          'groupOrder/enableDisableGroupOrderRecipient',
          {
            groupOrderId: this.groupOrderId,
            filterBy: this.currentFilter
          }
        )
      }
    },
    setSelected(value) {
      this.$store.dispatch('globalTracking/changeSearchTerm', '')
      this.currentFilter = value
      this.$store.dispatch('groupOrder/getGroupOrdersRecipientsList', {
        groupOrderId: this.groupOrderId,
        filterBy: this.currentFilter
      })
    }
  },
  watch: {
    searchTerm(value) {
      this.$store.dispatch('groupOrder/getGroupOrdersRecipientsList', {
        groupOrderId: this.groupOrderId,
        filterBy: recipientListFilterTypes.NameOrEmail,
        nameOrEmail: value
      })
    },
    '$route.query.isPending': {
      handler: function(value) {
        if (value) {
          this.setSelected('Pending')
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

h4 {
  color: $onyx;
  font-size: 2rem;
  font-weight: normal;
}

.modal-description {
  color: $onyx;
  padding-top: 1rem;
  padding-bottom: 2rem;
  font-weight: normal;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .search-region {
    width: 30%;
  }
}

.recipients-tab-menu {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 2.5rem 0;

  & ul {
    list-style: none;
    display: flex;
    align-items: center;
    padding: 0;
  }

  & ul > li {
    display: block;
    align-items: center;
    padding: 0rem 0rem 0rem 1.5rem;
    border-left: 3px solid transparent;
    line-height: 30px;
  }

  & ul > li > p {
    font-weight: 600;
    cursor: pointer;
    font-size: $body-base-small;
  }

  & ul > li > p:hover {
    text-decoration: underline;
  }
}

.email {
  display: block;
  width: 7rem;
  max-width: 7rem;
  word-wrap: break-word;
}

.icon-action-btn {
  display: flex !important;
  align-items: center;

  img {
    cursor: pointer;
  }

  .inactive {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

@media (max-width: 630px) {
  .group-order-recipient-info {
    padding-bottom: 20rem;
  }
}
@media (max-width: 860px) {
  .filters {
    display: flex;
    flex-direction: row;
  }
  .light {
    width: 150px;
    justify-content: end;
  }
  .filters .search-region {
    width: 50% !important;
  }
}
</style>
